/**
 * Contracts for built intangibles — development projects and the CPC 04 capitalisation gate
 * (ADR 0017, epic ALC-3485, slice ALC-3486).
 *
 * A bought intangible (a perpetual licence, a registered brand) already worked: one bill, one
 * review-queue verdict, one asset. What did not is the case a SaaS tenant actually has —
 * software or course content built in-house, whose cost arrives over months from payroll, PJ
 * collaborators and cloud invoices, and which becomes an asset only when it goes live. The
 * `IntangibleProject` is what carries that cost until then.
 *
 * **Amounts are decimal strings, fixed to 2 places**, as everywhere else in the module. A
 * project total is a sum over many months of many sources; sending it as a JSON number would
 * hand the browser a float to round.
 *
 * ## Confidentiality is a shape decision here, not a permission check
 *
 * People cost reaches a project through the allocation grid (ADR 0017 D4), so a
 * `PAYROLL_ALLOCATION` cost line holds one named person's pay. **No view in this file carries
 * a per-person amount**, and none may be added: the project reads group to competência and
 * source type inside the service, before anything leaves it. `gl:manage` is finance authority,
 * not HR authority — an accountant may capitalise the R$ 84.000 the grid produced without
 * being entitled to learn what Maria earns. The grouping, not the guard, is what makes that
 * true, because a guard has to be remembered on every new endpoint and a shape does not.
 */
// The two enums below come from `../generated/enums.js`, which the package barrel already
// re-exports. They are imported, never re-exported from here: `export *` from two modules that
// both name `IntangibleProjectStatus` makes the name ambiguous and drops it from `@alinea/types`.
import type {
  AssetArt42ExclusionSource,
  IntangibleProjectCostSourceType,
  IntangibleProjectStatus,
} from '../generated/enums.js';

// ─────────────────────────────────────────────────────────────────────────────
// Gate template — the checklist, per accounting framework
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One condition on a framework's capitalisation checklist (ADR 0017 D5).
 *
 * System reference data, identical for every tenant: the six CPC 04 §57 conditions are the
 * norm's own text. `code` is the stable key a `IntangibleProjectGateCheckView` points at and is
 * never renamed — the wording may be improved, the certification it backs may not be invalidated.
 */
export interface IntangibleGateTemplateItemView {
  code: string;
  sequence: number;
  name: string;
  description: string | null;
  /** Free-text citation, e.g. `CPC 04 (R1) §57(a)`. NOT NULL on the table — every row cites. */
  normReference: string;
}

/**
 * The checklist a framework requires.
 *
 * An **empty `items` array is a valid answer**, not a 404: it is what a framework nobody has
 * seeded yet looks like (US GAAP and NIF C-8 today). The refusal happens where it matters —
 * creating a project under such a framework fails with `INTANGIBLE_GATE_TEMPLATE_MISSING`
 * rather than letting a gate pass with nothing to tick.
 */
export interface IntangibleGateTemplateResponse {
  frameworkCode: string;
  items: IntangibleGateTemplateItemView[];
}

/** One condition an accountant certified as holding, and when. */
export interface IntangibleProjectGateCheckView {
  templateItemCode: string;
  frameworkCode: string;
  checkedAt: string;
  checkedByUserId: string | null;
}

/**
 * One §57 certification of the gate — the live one, or a superseded one (ADR 0017 D5, DEC-6).
 *
 * A change of the development date does not edit the previous judgement; it supersedes it and
 * records a new one. This view is what makes the earlier judgement readable afterwards, which
 * is what an auditor asks for when the capitalisation of a closed month is questioned: which
 * date was in force then, who signed it, and why it was later moved.
 *
 * No amounts, so `gl:read` is the whole guard here.
 */
export interface IntangibleProjectGateCertificationView {
  certificationId: string;
  /** `YYYY-MM-DD`. */
  developmentStartedOn: string;
  frameworkCode: string;
  certifiedAt: string;
  certifiedByUserId: string | null;
  /** Why the date was certified at this value. Required on a re-certification, else null. */
  reason: string | null;
  /** Null on the certification in force. */
  supersededAt: string | null;
  supersededByUserId: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Projects
// ─────────────────────────────────────────────────────────────────────────────

/**
 * How many distinct people must have contributed to a project in a competência before that
 * month's people cost may be shown as an amount (ADR 0017 D4, DEC-5, decided 2026-09-05).
 *
 * A `PAYROLL_ALLOCATION` or `PJ_BILL_LINE` total for a month is a sum of named people's pay
 * shares. At one contributor the sum *is* that person's share, disclosed to the cent to
 * anyone holding `gl:read` — which is finance authority, not HR authority. At two it is one
 * subtraction away for either of them. Three is the smallest floor at which the aggregate
 * still aggregates, and it is the floor the owner chose over the alternatives (a separate
 * permission key, or rounding the group to the nearest thousand).
 *
 * A constant rather than a tenant setting **for now**. Every tenant gets the same answer
 * today, which is the right default and the one that needs no migration; if a tenant with a
 * two-person engineering team asks for a lower floor, this becomes a column on
 * `AssetTenantPolicy` and this constant becomes its default. Nothing else has to move: the
 * suppression decision is taken in one place, `IntangibleProjectService`, and reaches the
 * screens as the `suppressed` flags below rather than as a number the client compares.
 */
export const INTANGIBLE_PEOPLE_COST_MIN_HEADCOUNT = 3;

/**
 * Source types whose cost is one or more named people's pay, and so is subject to the
 * headcount floor above. `AP_BILL_LINE` is a supplier's invoice and stays visible.
 */
export const INTANGIBLE_PEOPLE_COST_SOURCE_TYPES: readonly IntangibleProjectCostSourceType[] = [
  'PAYROLL_ALLOCATION',
  'PJ_BILL_LINE',
];

/**
 * A project's cost, rolled up to the grain the screens read it at.
 *
 * Competência and source type, never a person and never a document. `capitalisedAmount` and
 * `nonCapitalisedAmount` are split rather than netted because they are two different facts:
 * the first is on the balance sheet, the second went to the DRE in a month that is closed.
 *
 * There is deliberately **no count of the rows behind an amount**, and none may be added:
 * a `PAYROLL_ALLOCATION` group with a count of 1 is one named person's pay share, disclosed
 * exactly, to a reader holding only `gl:read` — the aggregate stops protecting anything at
 * n = 1. That residue was DEC-5 in the QA backlog and is now decided: below
 * `INTANGIBLE_PEOPLE_COST_MIN_HEADCOUNT` contributors the group arrives `suppressed`, with
 * **both amounts null**, and the screen says "Custo de pessoal: confidencial".
 */
export interface IntangibleProjectCostGroupView {
  /** `YYYY-MM`. */
  competencia: string;
  sourceType: IntangibleProjectCostSourceType;
  /** Null exactly when `suppressed` is true. */
  capitalisedAmount: string | null;
  /** Null exactly when `suppressed` is true. */
  nonCapitalisedAmount: string | null;
  /**
   * True when the amounts above are withheld because too few people stand behind them (DEC-5,
   * owner decision of 2026-09-05: "hide small groups"). Always `false` on `AP_BILL_LINE`.
   *
   * The shape arrived with slice 2 (ALC-3487) and the computation with the allocation grid in
   * slice 3 (ALC-3488), which is what first writes a `PAYROLL_ALLOCATION` line. A suppressed
   * group still names its competência and source type — the reader has to know a cost exists
   * in order to read the project at all — but the two amounts are `null`, and the UI renders
   * "Custo de pessoal: confidencial" rather than a number. A missing group and a suppressed
   * one are different statements, so the row is never dropped: hiding it would make the screen
   * claim the project had no people cost that month. A row count is never exposed either way:
   * "1 pessoa, R$ X" is one person's pay stated exactly.
   */
  suppressed: boolean;
}

/** The totals every project screen leads with. */
export interface IntangibleProjectTotalsView {
  /**
   * Capitalised cost in the current competência — what this month has added so far.
   *
   * **Null when `monthToDateSuppressed`**, and that is not decoration: this is a competência
   * total, so leaving it visible beside a suppressed people group would hand the reader the
   * suppressed amount by subtracting the month's visible `AP_BILL_LINE` groups from it.
   */
  monthToDateCapitalisedAmount: string | null;
  /** The current competência's people cost is below the headcount floor (DEC-5). */
  monthToDateSuppressed: boolean;
  /**
   * Capitalised cost since the project opened. **Null when `totalsSuppressed`.**
   *
   * The lifetime total was left visible at first, on the reasoning that spanning many months
   * blunts it. It does not. A project's *first* month is the whole of its lifetime, so this
   * number is that month's people cost exactly; and in any later month
   * `lifetime − Σ visible groups` returns the suppressed amount by subtraction. Whenever any
   * competência's people cost is suppressed (DEC-5), both lifetime totals are withheld too.
   *
   * Slice 4's go-live reclass therefore does **not** read the amount from this view: it asks
   * `IntangibleProjectService.getCapitalisedTotalForPosting`, which is service-internal, is
   * never suppressed, and never reaches a controller.
   */
  totalCapitalisedAmount: string | null;
  /**
   * Cost linked to the project but expensed, because it predates the gate (ADR 0017 D5).
   * **Null when `totalsSuppressed`** — same subtraction argument as above.
   */
  totalNonCapitalisedAmount: string | null;
  /**
   * At least one competência's people cost is below the headcount floor, so the lifetime
   * totals are withheld (DEC-5).
   *
   * Deliberately coarser than `monthToDateSuppressed`: one suppressed month anywhere in the
   * project's history is enough, because a lifetime total that includes it is a channel to it.
   */
  totalsSuppressed: boolean;
}

export interface IntangibleProjectView {
  projectId: string;
  entityId: string;
  classId: string;
  classCode: string;
  className: string;
  code: string;
  name: string;
  description: string | null;
  status: IntangibleProjectStatus;
  /** `YYYY-MM-DD`, or null while the project is still in research. */
  developmentStartedOn: string | null;
  /**
   * The project's own art. 42 answer, **resolved at creation** (ADR 0017 D9, DEC-7).
   *
   * Not a live inheritance: a project created while the entity answered `false` keeps `false`
   * when the entity later switches, because the fiscal treatment of the months it has already
   * run must not be reinterpreted by a decision taken afterwards. The entity default is a
   * default for new projects, nothing more.
   *
   * `null` only on rows created before that rule existed, where the entity default still
   * applies on read.
   */
  applyArt42Exclusion: boolean | null;
  /** The value in force: the stored one, or the entity default for a legacy `null`. */
  effectiveApplyArt42Exclusion: boolean;
  /**
   * The entity's art. 42 answer **as it stands now** (ALC-3490), independent of what this
   * project froze. `false` for an entity with no active `TAX_BASIS` book, which has no answer
   * to give.
   *
   * Deliberately NOT the same fact as `effectiveApplyArt42Exclusion`, which is the project's
   * own value and is what every fiscal decision reads. These two diverge exactly when the
   * entity changed regime after this project was created — the visible consequence of DEC-7,
   * and the thing a project screen needs in order to explain why two projects of one entity
   * are treated differently. Never read it to decide a fiscal treatment; it is for display.
   */
  entityArt42Default: boolean;
  /** Whether the stored value came from the entity's answer or was stated for this project. */
  applyArt42ExclusionSource: AssetArt42ExclusionSource;
  /**
   * When that answer was frozen. Read the entity policy `asOf` this instant to see the version
   * of the entity's election the project was created under. Null only on legacy rows.
   */
  art42ResolvedAt: string | null;
  /**
   * The `sequence` of the entity policy change in force when the answer was frozen — the exact
   * ledger row, so the join never depends on comparing timestamps. Null when no change had been
   * logged yet (unstated default: off) and on projects that override the entity answer.
   */
  art42PolicySequence: number | null;
  frameworkCode: string;
  assetId: string | null;
  /** When the write-off was recorded (an instant). */
  writtenOffAt: string | null;
  /**
   * The date the write-off takes effect (`YYYY-MM-DD`) — its journal's date, which may be
   * backdated into an open period. Null on projects not written off.
   */
  writeOffDate: string | null;
  writeOffReason: string | null;
  totals: IntangibleProjectTotalsView;
  createdAt: string;
  updatedAt: string | null;
}

/** A project plus everything the detail screen needs. Still totals only — see the file header. */
export interface IntangibleProjectDetailView extends IntangibleProjectView {
  /** The **live** certification's ticks, not every tick the project ever carried. */
  gateChecks: IntangibleProjectGateCheckView[];
  /** The framework's checklist, so the screen can show ticked and unticked side by side. */
  gateTemplate: IntangibleGateTemplateItemView[];
  /** Every certification, newest first — the live one plus each it superseded (DEC-6). */
  gateHistory: IntangibleProjectGateCertificationView[];
  costGroups: IntangibleProjectCostGroupView[];
}

/** Columns the projects listing may sort on. Every one is a column on the project row itself. */
export const INTANGIBLE_PROJECT_SORT_FIELDS = [
  'code',
  'name',
  'status',
  'createdAt',
  'developmentStartedOn',
] as const;
export type IntangibleProjectSortField = (typeof INTANGIBLE_PROJECT_SORT_FIELDS)[number];

export interface ListIntangibleProjectsRequest {
  entityId?: string;
  status?: IntangibleProjectStatus;
  classId?: string;
  /** Case-insensitive match on the project code or name. */
  search?: string;
  sortBy?: IntangibleProjectSortField;
  sortOrder?: 'asc' | 'desc';
  /**
   * One-based page. Sending `page` or `limit` switches the endpoint to paged mode: `limit`
   * defaults to 20 (max 100), `total` says how many pages exist and `truncated` is always
   * false. Without either, the response keeps the legacy capped shape callers like the
   * candidate-decision dialog rely on.
   */
  page?: number;
  limit?: number;
}

export interface ListIntangibleProjectsResponse {
  projects: IntangibleProjectView[];
  /**
   * True when the tenant holds more projects than this response carries, so `projects` is a
   * prefix of them (ALC-3491 round 2, CFO-B3).
   *
   * The list is capped server-side and ordered newest-first, so the rows that fall off are the
   * **oldest** — which on this module means the long-running projects carrying the largest
   * capitalised balances. Any figure a screen derives by summing `projects` is therefore a
   * subtotal, not a total, and a card labelled "Capitalizado em andamento" that printed it
   * silently would disagree with `Intangível em Andamento` in the razão with nothing on screen
   * explaining why. The flag exists so the reader is told, rather than the number being quietly
   * wrong; a cursor replaces it the day a tenant actually approaches the cap.
   *
   * Unpaged requests only: a paged response is a slice by request and always says false.
   */
  truncated: boolean;
  /** Projects matching the filters, across every page. */
  total: number;
}

export interface CreateIntangibleProjectRequest {
  entityId: string;
  /** Must resolve to a `FixedAssetClass` whose group is INTANGIBLE (ADR 0017 D2). */
  classId: string;
  code: string;
  name: string;
  description?: string;
  /**
   * Omit to take the entity's art. 42 answer as it stands today; send a boolean to state this
   * project's own. Either way the resolved value is **stored on the project** — a later change
   * to the entity default does not reach back into it (DEC-7).
   */
  applyArt42Exclusion?: boolean | null;
}

/**
 * Only the two free-text fields.
 *
 * Entity, class and framework are absent by design: changing any of them after cost has
 * accrued would move a balance between disclosure groups — or between legal entities — with no
 * journal to show for it. A project pointed at the wrong class is closed and reopened.
 */
export interface UpdateIntangibleProjectRequest {
  name?: string;
  description?: string | null;
}

/**
 * Opens the capitalisation gate (ADR 0017 D5).
 *
 * All six conditions in one call rather than one tick per request: §57 is a single judgement
 * about a single date, and a half-ticked checklist persisted across requests would be a state
 * the norm has no name for.
 */
export interface SetIntangibleProjectGateRequest {
  /** `YYYY-MM-DD`. Never earlier than the project's creation month, and never moved backwards. */
  developmentStartedOn: string;
  /** Every active template item code for the project's framework. */
  checkedItemCodes: string[];
  /**
   * Why the date is being certified at this value.
   *
   * Required when the project already carries a certification at a different date
   * (`INTANGIBLE_GATE_RECERTIFICATION_REASON_REQUIRED`): the first one is explained by the §57
   * checklist itself, a later one changes the month capitalisation starts in and needs a
   * reason an auditor can read. Optional on the first certification, and stored either way.
   */
  reason?: string;
}

export interface IntangibleProjectMutationResult {
  project: IntangibleProjectDetailView;
}

// ─────────────────────────────────────────────────────────────────────────────
// Go-live and write-off — the two ways a project closes (ADR 0017 D6, D7, ALC-3489)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Put the project into service: one reclass journal, one asset, and the project closes.
 *
 * ADR 0017 D6. The whole capitalised balance moves out of `FA_INTANGIBLE_IN_PROGRESS` into the
 * class's own cost account, and the `FixedAsset` is born with that cost, this date as its
 * acquisition and in-service date, and the class rule's useful life. **One project becomes one
 * asset and closes** — work after go-live is expense (CPC 04 §20) and a new version is a new
 * project, which is why there is no "re-open" on this contract.
 */
export interface GoLiveIntangibleProjectRequest {
  /**
   * `YYYY-MM-DD`. The day the thing became available for use, which is both the asset's
   * in-service date and the journal date of the reclass — so it must fall in an OPEN period, or
   * the GL refuses it with its own `JOURNAL_NO_OPEN_PERIOD`.
   *
   * It may not precede the last competência that capitalised cost
   * (`INTANGIBLE_PROJECT_GO_LIVE_BEFORE_LAST_COST`): an asset in service before the month its
   * own cost was incurred would start amortising a base the ledger had not yet assembled.
   */
  inServiceDate: string;
  /**
   * Override the class rule's useful life, in months, on the asset's statutory book rule.
   * Omit to take the class rule as it stands.
   */
  usefulLifeMonths?: number;
  /**
   * Why the life differs from the class rule. **Required whenever `usefulLifeMonths` differs
   * from what the class rule says** (`INTANGIBLE_PROJECT_LIFE_OVERRIDE_REASON_REQUIRED`) and
   * stored on the asset's book rule `overrideReason`, the registry's answer to "why is this
   * bem's régua not the class's". Distinct from `normReference`, which keeps the legislated
   * citation the class rule was copied from.
   */
  overrideReason?: string;
  /**
   * Residual value as a decimal fraction of cost (`"0.10"` is ten per cent), overriding the
   * class rule. Rare on an intangible — CPC 04 §100 presumes zero — and accepted because the
   * registration path it rides already accepts one.
   */
  residualRate?: string;
  /**
   * The managerial dimensions the bem is born with — and therefore every month of its
   * amortisation (ACC-12). Each one, independently: omitted inherits the single value every
   * capitalised cost carries; `null` means deliberately none. When omitted and the costs reached
   * the slot on more than one value, go-live is refused with
   * `INTANGIBLE_PROJECT_GO_LIVE_DIMENSION_AMBIGUOUS` (`dimension`, `candidates`). The
   * in-progress credit is not affected: it always clears each cost on the dimensions it came in.
   */
  costCenterId?: string | null;
  departmentId?: string | null;
  managerialAccountId?: string | null;
}

export interface GoLiveIntangibleProjectResult {
  project: IntangibleProjectDetailView;
  /** The bem the project became. `project.assetId` carries the same value. */
  assetId: string;
  /** Its plaqueta, reserved from the class code by the registration path. */
  assetCode: string;
  /**
   * The reclass journals, one per posted book that resolves both accounts. Under ALC-1993 an
   * entity has one book, so this is one id today.
   */
  journalIds: string[];
  /**
   * The amount reclassified — the project's live capitalised total, as a decimal string.
   *
   * **Null when `project.totals.totalsSuppressed`** (DEC-5). The journal carries the real
   * figure; this field is a response a `gl:manage` reader holds, and on a project whose people
   * cost sits below the headcount floor the lifetime total is withheld on every other surface,
   * so it is withheld here too rather than becoming a second door to the same number.
   */
  capitalisedAmount: string | null;
}

/**
 * Abandon the project: the balance goes to a loss account and the project closes (ADR 0017 D7).
 *
 * Cost lines are **not** deleted and not marked reversed. The money was really spent; what the
 * write-off says is that it never became an asset. That is a separate journal, not an unwinding
 * of the ones that recorded the spend.
 */
export interface WriteOffIntangibleProjectRequest {
  /**
   * Why the project is being abandoned. Required
   * (`INTANGIBLE_PROJECT_WRITE_OFF_REASON_REQUIRED`): CPC 04 §112 derecognition is a judgement,
   * and a balance moved to a loss account with nothing saying why is unauditable.
   */
  reason: string;
  /**
   * `YYYY-MM-DD` for the journal. Defaults to today. Must fall in an OPEN period — the write-off
   * belongs to the current period, never to the months the cost was incurred in.
   */
  writeOffDate?: string;
}

export interface WriteOffIntangibleProjectResult {
  project: IntangibleProjectDetailView;
  /** One per posted book that resolves both accounts; one today (ALC-1993). */
  journalIds: string[];
  /**
   * The amount written off — the project's live capitalised total, as a decimal string.
   * **Null when `project.totals.totalsSuppressed`** (DEC-5), same argument as go-live's
   * `capitalisedAmount`.
   */
  writtenOffAmount: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Entity policy — Lei 12.973/2014 art. 42
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The entity's art. 42 answer (ADR 0017 D9).
 *
 * `applyArt42DevelopmentExclusion` is **absent, not `false`**, for an entity with no
 * `TAX_BASIS` book. The two are different statements: `false` means "this entity files a
 * LALUR and chose not to take the exclusion", absent means "there is no fiscal rail here to
 * answer for". Rendering the second as the first would put a tax switch in front of an
 * accountant who has no tax book to apply it to.
 */
/**
 * An entity's assets policy **as of an instant** (ADR 0017 D9, DEC-7).
 *
 * `applyArt42DevelopmentExclusion` is the value in force at `asOf`, obtained by walking
 * `history` to the newest change with `effectiveFrom <= asOf` — not by reading a live row. The
 * live row is a cache of the latest change; the change log is the ledger. Asking with no `asOf`
 * asks about now, which is the common case and the default.
 */
export interface AssetEntityPolicyView {
  entityId: string;
  /** True exactly when the entity has an active `TAX_BASIS` measurement scope, today. */
  hasFiscalBook: boolean;
  applyArt42DevelopmentExclusion?: boolean;
  /** The instant this answer is given for. Echoes the request's `asOf`, or the read's own now. */
  asOf: string;
  /**
   * When the answer above took force — the `effectiveFrom` of the change that produced it.
   * `null` when no change precedes `asOf`, which is the entity's unstated default of off.
   */
  effectiveSince: string | null;
  /** `sequence` of the change that produced the answer above; `null` when none precedes `asOf`. */
  effectiveSequence: number | null;
  updatedAt: string | null;
  updatedByUserId: string | null;
  /**
   * Every change to this entity's policy, newest first — the whole ledger, not only the part
   * before `asOf`, so one read can explain both what was in force then and what happened since.
   */
  history: AssetEntityPolicyChangeView[];
  /**
   * True when the ledger holds more changes than `history` carries (newest first, capped). The
   * value and `effectiveSince` above are never computed from this page — they come from a
   * dedicated read of the row in force at `asOf` — so a truncated history never changes them.
   */
  historyTruncated: boolean;
}

/** One change to an entity's assets policy. Append-only — never updated once written. */
export interface AssetEntityPolicyChangeView {
  changeId: string;
  /** The setting that changed, by its API name (`applyArt42DevelopmentExclusion`). */
  field: string;
  /**
   * The value being replaced. The entity's first answer logs `"false"`, not `null`: a missing
   * policy row means "off" everywhere in this module, so there was an answer in force. Nullable
   * for a genuinely unknown prior, which nothing writes today.
   */
  previousValue: string | null;
  newValue: string;
  /**
   * Causal position among this entity's changes (1, 2, 3 …), assigned under the policy row
   * lock. This is the order that decides ties, not `changeId`, which is a random UUID.
   */
  sequence: number;
  /** When the row was written. */
  changedAt: string;
  /** When its value took force. Equal to `changedAt` — a change is never backdated. */
  effectiveFrom: string;
  changedByUserId: string | null;
  reason: string | null;
}

export interface ReadAssetEntityPolicyRequest {
  entityId: string;
  /** ISO timestamp. Answers the value in force at that instant. Defaults to now. */
  asOf?: string;
}

export interface SaveAssetEntityPolicyRequest {
  entityId: string;
  applyArt42DevelopmentExclusion: boolean;
  /** Why the entity's answer is changing. Recorded on the history entry when the value moves. */
  reason?: string;
  /**
   * ISO timestamp asserting when the change takes force. Optional, and it may not name an
   * instant before the change is made: a backdated policy change would re-decide the elections
   * of projects already created under the previous answer, which is exactly what DEC-7 removed.
   * Anything earlier is refused with `INTANGIBLE_ENTITY_POLICY_NOT_RETROACTIVE`.
   */
  effectiveFrom?: string;
}

export interface AssetEntityPolicyMutationResult {
  policy: AssetEntityPolicyView;
}
